import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Input, Modal, Card, Row, Col, Statistic, Tag, Space, Tooltip, Select } from 'antd';
import { 
  FaRegTrashAlt, 
  FaEdit, 
  FaUsers, 
  FaSearch, 
  FaSync, 
  FaUserPlus,
  FaFilter,
  FaIdCard,
  FaPhone,
  FaBuilding
} from 'react-icons/fa';
import { HiOutlineUserGroup } from 'react-icons/hi';
import ModalAdd from '../modal/add/AddCostumer';
import ModalEdit from '../modal/alter/AlterCostumer';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';
import '../../assets/css/Cliente.css';

const { Search } = Input;
const { Option } = Select;

const Cliente = () => {
  const [costumer, setCostumer] = useState([]);
  const [filteredCostumer, setFilteredCostumer] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalEditOpen, setIsModalEdit] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [partnerFilter, setPartnerFilter] = useState('');
  const [uniquePartners, setUniquePartners] = useState([]);
  const usuario = JSON.parse(localStorage.getItem('user'));

  // Estatísticas - apenas total de clientes
  const totalClientes = useMemo(() => costumer.length, [costumer]);

  const sms_error = (sms) => {
    toast.error(sms, {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const sms_success = (sms) => {
    toast.success(sms, {
      position: "top-right",
      autoClose: 2000,
    });
  };

  const fetchCostumer = async () => {
    setLoading(true);
    try {
      const dados = { id: usuario.id };
      const response = await Api.ListCostumers(dados);
      
      if (Array.isArray(response)) {
        const formattedCostumers = response.map((item, index) => ({
          key: item.id,
          id: item.id,
          id_partner: item.idPartner,
          nome: item.Nome,
          nif: item.Nif,
          contacto: item.Telefone,
          nome_parceiro: item.nome_parceiro || 'Sem parceiro',
          numero: index + 1,
          dataRegistro: item.dataRegistro || new Date().toISOString().split('T')[0]
        }));
        
        setCostumer(formattedCostumers);
        setFilteredCostumer(formattedCostumers);
        
        // Extrair parceiros únicos para o filtro
        const partners = [...new Set(response
          .filter(item => item.nome_parceiro)
          .map(item => item.nome_parceiro))];
        setUniquePartners(partners);
      } else {
        sms_error('Formato de resposta inválido');
      }
    } catch (error) {
      sms_error('Erro ao buscar clientes: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCostumer();
  }, []);

  // Função de busca global
  const handleSearch = (value) => {
    setSearchText(value);
    if (!value.trim()) {
      applyFilters({}, partnerFilter);
      return;
    }
    
    const filtered = costumer.filter(item =>
      item.nome?.toLowerCase().includes(value.toLowerCase()) ||
      item.nif?.includes(value) ||
      item.contacto?.includes(value) ||
      item.nome_parceiro?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredCostumer(filtered);
  };

  // Filtrar por campo específico
  const handleFilter = (field, value) => {
    const newFilters = { ...activeFilters, [field]: value };
    setActiveFilters(newFilters);
    applyFilters(newFilters, partnerFilter);
  };

  // Filtrar por parceiro
  const handlePartnerFilter = (value) => {
    setPartnerFilter(value);
    applyFilters(activeFilters, value);
  };

  // Aplicar todos os filtros
  const applyFilters = (filters, partner) => {
    let filtered = [...costumer];
    
    // Aplicar filtros de campos
    Object.entries(filters).forEach(([key, val]) => {
      if (val) {
        filtered = filtered.filter(item => 
          item[key]?.toString().toLowerCase().includes(val.toLowerCase())
        );
      }
    });
    
    // Aplicar filtro de parceiro
    if (partner) {
      filtered = filtered.filter(item => 
        item.nome_parceiro?.toLowerCase().includes(partner.toLowerCase())
      );
    }
    
    setFilteredCostumer(filtered);
  };

  // Limpar filtros
  const clearFilters = () => {
    setActiveFilters({});
    setPartnerFilter('');
    setSearchText('');
    setFilteredCostumer(costumer);
  };

  const handleAddClient = async (values) => {
    try {
      const costumerData = {
        Nome: values.nome,
        Nif: values.nif,
        Telefone: values.telefone,
        idPartner: values.parceiro,
      };
      const result = await Api.AddCostumer(costumerData);
      if (result.success) {
        sms_success('Cliente adicionado com sucesso!');
        fetchCostumer();
      } else {
        sms_error(result.message || 'Erro ao adicionar cliente');
      }
    } catch (error) {
      sms_error('Erro ao adicionar cliente: ' + error.message);
    }
  };

  const handleEditClient = async (values) => {
    try {
      const costumerData = {
        Nome: values.nome,
        Nif: values.nif,
        Telefone: values.telefone,
        idPartner: values.parceiro,
        id: values.id,
      };

      const result = await Api.AlterCliente(costumerData);
      if (result.success) {
        sms_success('Cliente editado com sucesso!');
        fetchCostumer();
      } else {
        sms_error(result.message || 'Erro ao editar cliente');
      }
    } catch (error) {
      sms_error('Erro ao editar cliente: ' + error.message);
    }
  };

  const handleDeleteClient = async (id) => {
    try {
      const costumerData = { id: id };
      const result = await Api.DeleteCliente(costumerData);
      if (result.success) {
        sms_success('Cliente excluído com sucesso!');
        fetchCostumer();
      } else {
        sms_error(result.message || 'Erro ao excluir cliente');
      }
    } catch (error) {
      sms_error('Erro ao excluir cliente: ' + error.message);
    }
  };

  const confirmDelete = (id, nome) => {
    setClientToDelete({ id, nome });
    setIsDeleteModalOpen(true);
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'numero',
      key: 'numero',
      width: 60,
      align: 'center',
      sorter: (a, b) => a.numero - b.numero,
    },
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
      width: '30%',
      render: (text) => (
        <div className="client-name">
        
          <span className="client-text">{text}</span>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por nome"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              handleFilter('nome', selectedKeys[0]);
              confirm();
            }}
          />
          <div className="filter-actions">
            <Button 
              type="primary" 
              onClick={() => {
                handleFilter('nome', selectedKeys[0]);
                confirm();
              }}
            >
              Filtrar
            </Button>
            <Button onClick={() => {
              clearFilters();
              handleFilter('nome', '');
            }}>
              Limpar
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
    {
      title: 'NIF',
      dataIndex: 'nif',
      key: 'nif',
      width: '15%',
      align: 'center',
      render: (nif) => (
        <Tag 
          
          color={nif && nif.trim() !== '' ? 'blue' : 'default'}
          className="nif-tag"
        >
          {nif && nif.trim() !== '' ? nif : 'Não informado'}
        </Tag>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por NIF"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              handleFilter('nif', selectedKeys[0]);
              confirm();
            }}
          />
          <div className="filter-actions">
            <Button 
              type="primary" 
              onClick={() => {
                handleFilter('nif', selectedKeys[0]);
                confirm();
              }}
            >
              Filtrar
            </Button>
            <Button onClick={() => {
              clearFilters();
              handleFilter('nif', '');
            }}>
              Limpar
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
    {
      title: 'Contacto',
      dataIndex: 'contacto',
      key: 'contacto',
      width: '15%',
      align: 'center',
      render: (contacto) => (
        <div className="contact-info">
          
          <span>{contacto && contacto.trim() !== '' ? contacto : 'Não informado'}</span>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por contacto"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              handleFilter('contacto', selectedKeys[0]);
              confirm();
            }}
          />
          <div className="filter-actions">
            <Button 
              type="primary" 
              onClick={() => {
                handleFilter('contacto', selectedKeys[0]);
                confirm();
              }}
            >
              Filtrar
            </Button>
            <Button onClick={() => {
              clearFilters();
              handleFilter('contacto', '');
            }}>
              Limpar
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
    {
      title: 'Parceiro',
      dataIndex: 'nome_parceiro',
      key: 'nome_parceiro',
      width: '20%',
      align: 'center',
      render: (nome_parceiro) => (
        <div className="partner-info">
        
          <span className="partner-name">{nome_parceiro}</span>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por parceiro"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              handleFilter('nome_parceiro', selectedKeys[0]);
              confirm();
            }}
          />
          <div className="filter-actions">
            <Button 
              type="primary" 
              onClick={() => {
                handleFilter('nome_parceiro', selectedKeys[0]);
                confirm();
              }}
            >
              Filtrar
            </Button>
            <Button onClick={() => {
              clearFilters();
              handleFilter('nome_parceiro', '');
            }}>
              Limpar
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: '15%',
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Editar cliente">
            <Button
              type="primary"
              
              icon={<FaEdit />}
              size="small"
              onClick={() => {
                setSelectedClient(record);
                setIsModalEdit(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Excluir cliente">
            <Button
              type="primary"
              danger
              
              icon={<FaRegTrashAlt />}
              size="small"
              onClick={() => confirmDelete(record.key, record.nome)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="cliente-container">
      <ToastContainer />
      
 

      {/* Cards de estatísticas - apenas total */}
      <Row gutter={16} className="stats-row">
        <Col span={24}>
          <Card className="stat-card total-card">
            <Statistic
              title="Total de Clientes"
              value={totalClientes}
              prefix={<FaUsers />}
              valueStyle={{ color: '#1890ff', fontSize: '36px' }}
            />
            <div className="stat-trend">
              <span>Total de clientes registrados</span>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Barra de ferramentas com filtros */}
      <Card className="toolbar-card">
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Search
              placeholder="Buscar clientes por nome, NIF, contacto ou parceiro..."
              prefix={<FaSearch />}
              enterButton="Buscar"
              size="large"
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              onSearch={handleSearch}
              allowClear
            />
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<FaUserPlus />}
              size="large"
              onClick={() => setIsModalOpen(true)}
            >
              Novo Cliente
            </Button>
          </Col>
        </Row>
        
        {/* Filtros ativos */}
        {Object.keys(activeFilters).length > 0 && (
          <div className="active-filters">
            <span className="filters-label">Filtros ativos:</span>
            {Object.entries(activeFilters).map(([key, value]) => (
              value && (
                <Tag 
                  key={key} 
                  closable 
                  onClose={() => handleFilter(key, '')}
                  color="blue"
                >
                  {key}: {value}
                </Tag>
              )
            ))}
            <Button type="link" size="small" onClick={clearFilters}>
              Limpar todos
            </Button>
          </div>
        )}
      </Card>



      {/* Tabela de clientes */}
      <Card className="table-card">
        <Table
          columns={columns}
          dataSource={filteredCostumer}
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} de ${total} clientes`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 1000 }}
          rowClassName={(record, index) => 
            index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
          }
          locale={{
            emptyText: searchText || Object.keys(activeFilters).length > 0 || partnerFilter 
              ? 'Nenhum cliente encontrado com os filtros atuais' 
              : 'Nenhum cliente cadastrado',
          }}
        />
      </Card>

      {/* Modais */}
      <ModalAdd
        isVisible={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={(values) => {
          handleAddClient(values);
          setIsModalOpen(false);
        }}
      />

      <ModalEdit
        isVisible={isModalEditOpen}
        selectedClient={selectedClient}
        onClose={() => setIsModalEdit(false)}
        onSubmit={(values) => {
          handleEditClient(values);
          setIsModalEdit(false);
        }}
      />

      <Modal
        title="Confirmar Exclusão"
        visible={isDeleteModalOpen}
        onOk={() => {
          handleDeleteClient(clientToDelete.id);
          setIsDeleteModalOpen(false);
        }}
        onCancel={() => setIsDeleteModalOpen(false)}
        okText="Sim, excluir"
        cancelText="Cancelar"
        okType="danger"
      >
        <div className="delete-modal-content">
          <div className="warning-icon">⚠️</div>
          <p>
            Tem certeza que deseja excluir o cliente 
            <strong> {clientToDelete?.nome}</strong>?
          </p>
          <p className="delete-warning">
            Esta ação não pode ser desfeita.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Cliente;